import { useMemo, useRef, useState } from 'react';

import { brl } from '../utils/formato';
import { Tabela, Th } from './common';

/**
 * Como o título foi se preenchendo, competência a competência.
 *
 * **Cada coluna é o título inteiro**, segmentado pelo que já aconteceu com
 * ele. Não é uma coluna por entrada: é o acumulado, então o que se lê é o
 * título enchendo mês a mês até o topo — e o que sobra em cinza é o que ainda
 * falta receber.
 *
 * A decisão que governa a leitura: **`Recuperado` está DENTRO de `Recebido`.**
 * O dinheiro do recurso voltou num demonstrativo posterior e já foi contado
 * como recebimento. Por isso a faixa mostrada é o recebido **líquido de
 * recuperação**, com o recuperado recortado ao lado — as cinco faixas somam
 * exatamente o título, e somar "recebido + recuperado" por fora dobraria o
 * que voltou.
 *
 * Paleta conferida com o validador do guia de visualização (modo claro,
 * categórica): CVD ΔE 8,2 no pior par adjacente, visão normal 18,0, contraste
 * ≥ 3:1 em todas. O ΔE na faixa 8 exige codificação secundária, que esta
 * figura tem de sobra: legenda, rótulo direto, vão de 2px entre as faixas e
 * a visão de tabela.
 */
const FAIXAS = [
  {
    chave: 'acum_recebido_liquido',
    rotulo: 'Recebido',
    cor: '#17539b',
    ajuda: 'O que a operadora pagou, já descontado o que voltou por recurso.',
  },
  {
    chave: 'acum_recuperado',
    rotulo: 'Recuperado',
    cor: '#128a5e',
    ajuda:
      'Glosa que voltou a ser paga depois do recurso. Faz parte do recebido — '
      + 'aparece à parte só para ficar visível.',
  },
  {
    chave: 'acum_glosado',
    rotulo: 'Glosado',
    cor: '#9a5b06',
    ajuda: 'O que a operadora recusou e ainda não voltou.',
  },
  {
    chave: 'acum_retido',
    rotulo: 'Retido',
    cor: '#6b4ea8',
    ajuda: 'Retenção judicial. Só entra por lançamento manual.',
  },
  {
    chave: 'acum_em_aberto',
    rotulo: 'Em aberto',
    // Neutro de propósito: não é uma série disputando identidade, é a
    // ausência — o pedaço do título sobre o qual nada aconteceu ainda.
    cor: '#dde3eb',
    neutro: true,
    ajuda: 'O que falta receber. Nem pago, nem recusado, nem retido.',
  },
];

const L = { topo: 28, base: 44, esq: 72, dir: 12 };
const ALTURA = 300;

const num = (v) => Number(v || 0);

export default function GraficoEvolucaoTitulo({ meses, totalCobrado }) {
  const [tabela, setTabela] = useState(false);
  const [sobre, setSobre] = useState(null);
  const svgRef = useRef(null);

  // O teto é o título. Sem ele (Bioslab ainda não respondeu), vira a maior
  // pilha — a figura continua legível e a tela avisa à parte que falta o
  // total. Inventar um teto faria as proporções mentirem.
  const teto = useMemo(() => {
    const t = num(totalCobrado);
    if (t > 0) return t;
    return Math.max(
      1,
      ...meses.map((m) =>
        FAIXAS.reduce((s, f) => s + Math.max(0, num(m[f.chave])), 0)
      )
    );
  }, [meses, totalCobrado]);

  if (!meses?.length) {
    return (
      <p style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
        Nenhuma competência com movimento neste título ainda.
      </p>
    );
  }

  const larguraUtil = 900 - L.esq - L.dir;
  const passo = larguraUtil / meses.length;
  const barra = Math.min(72, passo * 0.62);
  const escala = (v) => (num(v) / teto) * (ALTURA - L.topo - L.base);

  const marcas = [0, 0.25, 0.5, 0.75, 1].map((p) => ({
    p,
    y: L.topo + (1 - p) * (ALTURA - L.topo - L.base),
    valor: teto * p,
  }));

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <Legenda />
        <button
          type="button"
          className="ff-btn ff-btn--secundario ff-btn--pequeno"
          onClick={() => setTabela((v) => !v)}
        >
          <i
            className={`bi ${tabela ? 'bi-bar-chart' : 'bi-table'} me-1`}
            aria-hidden="true"
          />
          {tabela ? 'Ver gráfico' : 'Ver como tabela'}
        </button>
      </div>

      {tabela ? (
        <TabelaDaSerie meses={meses} />
      ) : (
        <div style={{ position: 'relative' }}>
          <svg
            ref={svgRef}
            viewBox={`0 0 900 ${ALTURA}`}
            preserveAspectRatio="xMidYMid meet"
            style={{ width: '100%', height: 'auto', display: 'block' }}
            role="img"
            aria-label={
              `Composição do título por competência, de ${meses[0].competencia}`
              + ` a ${meses[meses.length - 1].competencia}.`
            }
          >
            {/* Grade recessiva: referência, não conteúdo. */}
            {marcas.map((m) => (
              <g key={m.p}>
                <line
                  x1={L.esq}
                  x2={900 - L.dir}
                  y1={m.y}
                  y2={m.y}
                  stroke="var(--ff-border-soft)"
                  strokeWidth="1"
                />
                <text
                  x={L.esq - 8}
                  y={m.y + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill="var(--ff-muted)"
                  fontFamily="var(--ff-mono)"
                >
                  {m.p === 0 ? '0' : abreviado(m.valor)}
                </text>
              </g>
            ))}

            {meses.map((m, i) => {
              const cx = L.esq + passo * i + passo / 2;
              const x = cx - barra / 2;
              let y = ALTURA - L.base;
              const pedacos = [];
              for (const f of FAIXAS) {
                const v = Math.max(0, num(m[f.chave]));
                if (v <= 0) continue;
                const h = escala(v);
                y -= h;
                pedacos.push({ f, v, y, h });
              }
              const recebidoPct =
                teto > 0
                  ? Math.round(
                      ((num(m.acum_recebido_liquido) + num(m.acum_recuperado))
                        / teto) * 100
                    )
                  : null;
              return (
                <g
                  key={m.competencia}
                  onMouseEnter={(e) => setSobre({ m, x: e.clientX, y: e.clientY })}
                  onMouseMove={(e) => setSobre({ m, x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => setSobre(null)}
                >
                  {/* Alvo de hover maior que a marca, como manda o guia. */}
                  <rect
                    x={cx - passo / 2}
                    y={L.topo}
                    width={passo}
                    height={ALTURA - L.topo - L.base}
                    fill="transparent"
                  />
                  {pedacos.map(({ f, y: py, h }) => (
                    <rect
                      key={f.chave}
                      x={x}
                      y={py}
                      width={barra}
                      /* O vão de 2px entre faixas é o que separa duas cores
                         vizinhas sem depender de o olho distingui-las. */
                      height={Math.max(1, h - 2)}
                      rx={3}
                      fill={f.cor}
                    />
                  ))}
                  <text
                    x={cx}
                    y={ALTURA - L.base + 16}
                    textAnchor="middle"
                    fontSize="11"
                    fill="var(--ff-muted)"
                    fontFamily="var(--ff-mono)"
                  >
                    {m.competencia}
                  </text>
                  {/* Rótulo direto: o número que resume a coluna, e só ele.
                      Um valor em cada faixa viraria ruído. */}
                  {recebidoPct !== null && (
                    <text
                      x={cx}
                      y={L.topo - 8}
                      textAnchor="middle"
                      fontSize="11"
                      fontWeight="600"
                      fill="var(--ff-ink)"
                      fontFamily="var(--ff-mono)"
                    >
                      {recebidoPct}%
                    </text>
                  )}
                </g>
              );
            })}

            <line
              x1={L.esq}
              x2={900 - L.dir}
              y1={ALTURA - L.base}
              y2={ALTURA - L.base}
              stroke="var(--ff-border-field)"
              strokeWidth="1"
            />
          </svg>

          {sobre && <Balao dado={sobre} teto={teto} />}
          <p
            style={{ fontSize: 12, color: 'var(--ff-muted)', margin: '6px 0 0' }}
          >
            Cada coluna é o título inteiro naquele mês, acumulado. O número
            acima é quanto já entrou.
          </p>
        </div>
      )}
    </div>
  );
}

function Legenda() {
  return (
    <ul
      className="d-flex flex-wrap gap-3 m-0 p-0"
      style={{ listStyle: 'none', fontSize: 12 }}
    >
      {FAIXAS.map((f) => (
        <li key={f.chave} className="d-flex align-items-center gap-1" title={f.ajuda}>
          <span
            aria-hidden="true"
            style={{
              width: 10,
              height: 10,
              borderRadius: 3,
              background: f.cor,
              border: f.neutro ? '1px solid var(--ff-border-field)' : 'none',
              display: 'inline-block',
            }}
          />
          {/* O texto fica em tinta, nunca na cor da série. */}
          <span style={{ color: 'var(--ff-ink)' }}>{f.rotulo}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * `position: fixed` porque o gráfico vive dentro de um `.ff-painel`, que tem
 * `overflow: hidden` — um balão absoluto sairia recortado. É a mesma regra
 * que o componente `Ajuda` e o `SelecaoBuscavel` já seguem nesta base.
 */
function Balao({ dado, teto }) {
  const { m, x, y } = dado;
  const entrou = num(m.recebido);
  return (
    <div
      role="tooltip"
      style={{
        position: 'fixed',
        left: Math.min(x + 14, window.innerWidth - 270),
        top: Math.min(y + 14, window.innerHeight - 220),
        width: 252,
        zIndex: 1080,
        background: 'var(--ff-surface)',
        border: '1px solid var(--ff-border)',
        borderRadius: 10,
        boxShadow: 'var(--ff-shadow-card)',
        padding: '10px 12px',
        pointerEvents: 'none',
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 13 }}>{m.competencia}</div>
      {entrou > 0 && (
        <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
          entrou neste mês: {brl(entrou)}
        </div>
      )}
      <table style={{ width: '100%', marginTop: 6, fontSize: 12 }}>
        <tbody>
          {FAIXAS.map((f) => (
            <tr key={f.chave}>
              <td style={{ paddingRight: 6, width: 12 }}>
                <span
                  aria-hidden="true"
                  style={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: f.cor,
                  }}
                />
              </td>
              <td style={{ color: 'var(--ff-ink)' }}>{f.rotulo}</td>
              <td className="ff-mono" style={{ textAlign: 'right' }}>
                {m[f.chave] === null || m[f.chave] === undefined
                  ? '—'
                  : brl(m[f.chave])}
              </td>
            </tr>
          ))}
          <tr>
            <td />
            <td style={{ fontWeight: 600, paddingTop: 4 }}>Título</td>
            <td
              className="ff-mono"
              style={{ textAlign: 'right', fontWeight: 600, paddingTop: 4 }}
            >
              {brl(teto)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** A mesma série em números — exigência de acessibilidade do guia. */
function TabelaDaSerie({ meses }) {
  return (
    <Tabela estreita>
      <thead>
        {/* sem-ordenacao: é uma série temporal acumulada; reordenar faria o
            acumulado deixar de acumular, como no histórico do protocolo. */}
        <tr>
          <Th>Competência</Th>
          <Th alinhar="right">Entrou no mês</Th>
          {FAIXAS.map((f) => (
            <Th key={f.chave} alinhar="right">
              {f.rotulo}
            </Th>
          ))}
        </tr>
      </thead>
      <tbody>
        {meses.map((m) => (
          <tr key={m.competencia}>
            <td className="col-meta ff-mono">{m.competencia}</td>
            <td className="col-valor">{brl(m.recebido)}</td>
            {FAIXAS.map((f) => (
              <td key={f.chave} className="col-valor">
                {m[f.chave] === null || m[f.chave] === undefined
                  ? '—'
                  : brl(m[f.chave])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </Tabela>
  );
}

function abreviado(v) {
  const n = Number(v || 0);
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace('.', ',')}M`;
  if (n >= 1000) return `${Math.round(n / 1000)}k`;
  return String(Math.round(n));
}
